/**
 * Home — Landing/login page.
 * Full-screen corporate-elegant design with WMP branding.
 * Authenticated users redirect to /dashboard.
 * Demo mode redirects immediately.
 */

import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { Navigate } from "react-router-dom";
import { isDemoMode } from "../utils/PowerBIApiCall";
import { loginRequest } from "../authConfig";

/** Official WM Partners icon */
const WmpLogo = ({ className = "w-16 h-16" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 1080 1080"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#fff"
      d="M855.9,164.2c-43.3,75.7-101.1,176.7-152.8,266.9h0l-98.6-172.2h-129.4l-98.6,172.2-98.6-172.2-13.2-23h-124.9l13.2,23,156.6,273.6h133.8l96.4-168.4,96.4,168.4h133.8c5.5-9.7,210.7-368.3,210.7-368.3h-124.8Z"
    />
    <path
      fill="#fff"
      d="M926.4,821.1l-156.3-273.1h-133.8l-96.4,168.4-96.4-168.4h-133.8c-22.6,39.5-210.5,367.8-210.5,367.8h124.9c45.9-80.4,101.2-176.9,152.5-266.5h0s98.3,171.8,98.3,171.8h129.9l98.3-171.8,98.3,171.8,13.2,23h124.9l-13.2-23Z"
    />
  </svg>
);

/** Decorative floating shapes for depth */
const BackgroundShapes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute -top-1/4 -right-1/4 w-[800px] h-[800px] border border-white/[0.04] rounded-full" />
    <div className="absolute -bottom-1/3 -left-1/4 w-[600px] h-[600px] border border-white/[0.03] rounded-full" />
    <div
      className="absolute top-0 right-0 w-1/2 h-full"
      style={{
        background:
          "linear-gradient(135deg, transparent 40%, rgba(74,127,181,0.08) 100%)",
      }}
    />
  </div>
);

function LoginContent() {
  const { instance } = useMsal();

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(console.error);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-800 to-wmp-800 flex flex-col relative">
      <BackgroundShapes />

      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />

      {/* Main content — vertically centered */}
      <div className="flex-1 flex items-center justify-center relative z-10 px-6">
        <div className="max-w-md w-full">
          {/* Logo + Brand */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/[0.08] backdrop-blur-sm border border-white/[0.1] mb-6">
              <WmpLogo className="w-12 h-12" />
            </div>
            <h1 className="text-3xl font-light text-white tracking-tight">
              WM Partners
            </h1>
            <div className="w-12 h-[2px] bg-wmp-400 mx-auto mt-4 mb-3 rounded-full" />
            <p className="text-wmp-200/70 text-sm tracking-wide uppercase font-medium">
              Portfolio Dashboard
            </p>
          </div>

          {/* Sign-in card */}
          <div className="opacity-0 animate-fade-in-up stagger-2">
            <div className="bg-white/[0.06] backdrop-blur-md border border-white/[0.1] rounded-2xl p-8">
              <h2 className="text-white text-lg font-semibold mb-2">
                Sign in to continue
              </h2>
              <p className="text-wmp-200/70 text-sm mb-8 leading-relaxed">
                Access financial performance data across all portfolio
                companies.
              </p>

              <button
                onClick={handleLogin}
                className="w-full flex items-center justify-center gap-3 bg-wmp-500 hover:bg-wmp-400 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-wmp-500/25 focus:outline-none focus:ring-2 focus:ring-wmp-400/50 focus:ring-offset-2 focus:ring-offset-transparent"
              >
                {/* Microsoft logo */}
                <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                Sign in with Microsoft
              </button>

              <p className="text-wmp-200/60 text-[11px] text-center mt-5">
                Secured by Azure Active Directory
              </p>
            </div>
          </div>

          {/* Feature hints */}
          <div className="mt-10 grid grid-cols-3 gap-4 opacity-0 animate-fade-in stagger-4">
            <div className="text-center">
              <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center mx-auto mb-2">
                <svg
                  className="w-4 h-4 text-wmp-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <p className="text-wmp-200/70 text-[10px] font-medium uppercase tracking-wider">
                Real-time
              </p>
            </div>
            <div className="text-center">
              <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center mx-auto mb-2">
                <svg
                  className="w-4 h-4 text-wmp-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <p className="text-wmp-200/70 text-[10px] font-medium uppercase tracking-wider">
                Analytics
              </p>
            </div>
            <div className="text-center">
              <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center mx-auto mb-2">
                <svg
                  className="w-4 h-4 text-wmp-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <p className="text-wmp-200/70 text-[10px] font-medium uppercase tracking-wider">
                Secure
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer bar */}
      <div className="relative z-10 border-t border-white/[0.06] py-4 px-6">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <p className="text-wmp-200/60 text-[11px]">
            &copy; {new Date().getFullYear()} WM Partners, LP
          </p>
          <p className="text-wmp-200/60 text-[11px]">Aventura, FL</p>
        </div>
      </div>
    </div>
  );
}

export function Home() {
  // In demo mode, redirect straight to dashboard
  if (isDemoMode()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        <Navigate to="/dashboard" replace />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <LoginContent />
      </UnauthenticatedTemplate>
    </>
  );
}
